"use client";

import { c as _c } from "react/compiler-runtime";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { type QueryKey, useQuery, useQueryClient, useSuspenseQuery, type UseSuspenseQueryOptions } from "@tanstack/react-query";
import posthogClient from "posthog-js";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { CreateIssueButton } from "@/components/create-issue-button";
import { CertificatesSection } from "@/components/domain/certificates/certificates-section";
import { CertificatesSectionSkeleton } from "@/components/domain/certificates/certificates-section-skeleton";
import { DnsSection } from "@/components/domain/dns/dns-section";
import { DnsSectionSkeleton } from "@/components/domain/dns/dns-section-skeleton";
import { HeadersSection } from "@/components/domain/headers/headers-section";
import { HeadersSectionSkeleton } from "@/components/domain/headers/headers-section-skeleton";
import { HostingSection } from "@/components/domain/hosting/hosting-section";
import { HostingSectionSkeleton } from "@/components/domain/hosting/hosting-section-skeleton";
import { RegistrationSection } from "@/components/domain/registration/registration-section";
import { RegistrationSectionSkeleton } from "@/components/domain/registration/registration-section-skeleton";
import { DomainReportHeader } from "@/components/domain/report-header";
import { SectionNav } from "@/components/domain/report-nav";
import { SectionErrorBoundary } from "@/components/domain/report-section-error-boundary";
import { SectionFailedAlert } from "@/components/domain/section-failed-alert";
import { SeoSection } from "@/components/domain/seo/seo-section";
import { SeoSectionSkeleton } from "@/components/domain/seo/seo-section-skeleton";
import { DomainUnregisteredCard } from "@/components/domain/unregistered-card";
import { useSectionTracking } from "@/hooks/use-section-tracking";
import { getLookupErrorMessage } from "@/lib/constants/lookup-errors";
import { sections } from "@/lib/constants/sections";
import { useSearchHistoryStore } from "@/lib/stores/search-history-store";
import { useTRPC } from "@/lib/trpc/client";
import type { Section as SectionId } from "@domainstack/constants";
import type { LookupError, LookupOutcome } from "@domainstack/core/lookup";
import { Button } from "@domainstack/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@domainstack/ui/empty";
const SECTION_IDS = Object.keys(sections);
const staticQueryOptions = {
  staleTime: Number.POSITIVE_INFINITY,
  retry: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false
} as const;
type Trpc = ReturnType<typeof useTRPC>;

/**
 * A report section that loads after registration confirms the domain exists.
 * `sectionName` labels its error boundary (and the exception analytics).
 */
function reportSection<T, TError, TKey extends QueryKey>({
  id,
  sectionName,
  queryOptions,
  Section,
  Skeleton
}: {
  id: Exclude<SectionId, "registration">;
  sectionName: string;
  queryOptions: (trpc: Trpc, domain: string) => UseSuspenseQueryOptions<LookupOutcome<T>, TError, LookupOutcome<T>, TKey>;
  Section: React.ComponentType<{
    domain: string;
    data: NoInfer<T>;
  }>;
  Skeleton: React.ComponentType;
}) {
  function Loaded(t0) {
    const $ = _c(8);
    const {
      domain
    } = t0;
    const trpc = useTRPC();
    let t1;
    if ($[0] !== domain || $[1] !== trpc) {
      t1 = queryOptions(trpc, domain);
      $[0] = domain;
      $[1] = trpc;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const {
      data
    } = useSuspenseQuery(t1);
    if (!data.success) {
      let t2;
      if ($[3] !== data.error) {
        t2 = <SectionFailedAlert section={sections[id]} error={data.error} />;
        $[3] = data.error;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      return t2;
    }
    let t2;
    if ($[5] !== data.data || $[6] !== domain) {
      t2 = <Section domain={domain} data={data.data} />;
      $[5] = data.data;
      $[6] = domain;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  // Distinct names in React DevTools and error-boundary component stacks.
  Loaded.displayName = `Suspended${sectionName}Section`;
  return {
    id,
    sectionName,
    Loaded,
    Skeleton
  };
}

/**
 * Every section after registration, in page order. The `queryOptions` params
 * are annotated because TypeScript can't infer `T` through an unannotated callback.
 */
const REPORT_SECTIONS = [reportSection({
  id: "hosting",
  sectionName: "Hosting",
  queryOptions: (trpc: Trpc, domain: string) => trpc.domain.getHosting.queryOptions({
    domain
  }, staticQueryOptions),
  Section: HostingSection,
  Skeleton: HostingSectionSkeleton
}), reportSection({
  id: "dns",
  sectionName: "DNS",
  queryOptions: (trpc: Trpc, domain: string) => trpc.domain.getDnsRecords.queryOptions({
    domain
  }, staticQueryOptions),
  Section: DnsSection,
  Skeleton: DnsSectionSkeleton
}), reportSection({
  id: "certificates",
  sectionName: "Certificates",
  queryOptions: (trpc: Trpc, domain: string) => trpc.domain.getCertificates.queryOptions({
    domain
  }, staticQueryOptions),
  Section: CertificatesSection,
  Skeleton: CertificatesSectionSkeleton
}), reportSection({
  id: "headers",
  sectionName: "Headers",
  queryOptions: (trpc: Trpc, domain: string) => trpc.domain.getHeaders.queryOptions({
    domain
  }, staticQueryOptions),
  Section: HeadersSection,
  Skeleton: HeadersSectionSkeleton
}), reportSection({
  id: "seo",
  sectionName: "SEO",
  queryOptions: (trpc: Trpc, domain: string) => trpc.domain.getSeo.queryOptions({
    domain
  }, staticQueryOptions),
  Section: SeoSection,
  Skeleton: SeoSectionSkeleton
})];
function useDomainReportTracking(domain, isRegistered) {
  const $ = _c(14);
  const addDomainToHistory = useSearchHistoryStore(_temp);
  let t0;
  let t1;
  if ($[0] !== addDomainToHistory || $[1] !== domain || $[2] !== isRegistered) {
    t0 = () => {
      if (isRegistered) {
        addDomainToHistory(domain);
      }
    };
    t1 = [isRegistered, domain, addDomainToHistory];
    $[0] = addDomainToHistory;
    $[1] = domain;
    $[2] = isRegistered;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  const viewedDomainRef = useRef(null);
  let t2;
  let t3;
  if ($[5] !== domain || $[6] !== isRegistered) {
    t2 = () => {
      if (!isRegistered || viewedDomainRef.current === domain) {
        return;
      }
      viewedDomainRef.current = domain;
      posthogClient.capture("report_viewed", {
        domain
      });
    };
    t3 = [domain, isRegistered];
    $[5] = domain;
    $[6] = isRegistered;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const {
    activeSection,
    scrollToSection
  } = useSectionTracking(SECTION_IDS);
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = headerElement => {
      if (!headerElement) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        setIsHeaderVisible(entries[0].isIntersecting);
      }, {
        threshold: 0,
        rootMargin: "-10px 0px 0px 0px"
      });
      observer.observe(headerElement);
      return () => {
        observer.disconnect();
        setIsHeaderVisible(true);
      };
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const headerRef = t4;
  let t5;
  if ($[10] !== activeSection || $[11] !== isHeaderVisible || $[12] !== scrollToSection) {
    t5 = {
      headerRef,
      isHeaderVisible,
      activeSection,
      scrollToSection
    };
    $[10] = activeSection;
    $[11] = isHeaderVisible;
    $[12] = scrollToSection;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function _temp(s) {
  return s.addDomain;
}
function getReportErrorDescription({
  isRegistrationError,
  registrationError,
  lookupFailed,
  registration
}: {
  isRegistrationError: boolean;
  registrationError: unknown;
  lookupFailed: boolean;
  registration: {
    success: false;
    error: LookupError;
  } | {
    success: true;
  } | null | undefined;
}) {
  if (process.env.NODE_ENV === "development" && isRegistrationError && registrationError instanceof Error) {
    return registrationError.message;
  }
  if (lookupFailed && registration && !registration.success) {
    return getLookupErrorMessage(registration.error);
  }
  return "We couldn't fetch registration data for this domain. Please try again.";
}
function DomainReportLoadError(t0) {
  const $ = _c(19);
  const {
    domain,
    registrationError,
    description
  } = t0;
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <EmptyMedia variant="icon"><IconAlertTriangle /></EmptyMedia>;
    t2 = <EmptyTitle>Failed to load domain report</EmptyTitle>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== description) {
    t3 = <EmptyHeader>{t1}{t2}<EmptyDescription>{description}</EmptyDescription></EmptyHeader>;
    $[2] = description;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== domain || $[5] !== queryClient || $[6] !== trpc.domain.getRegistration) {
    t4 = () => queryClient.invalidateQueries(trpc.domain.getRegistration.queryFilter({
      domain
    }));
    $[4] = domain;
    $[5] = queryClient;
    $[6] = trpc.domain.getRegistration;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IconRefresh />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4) {
    t6 = <Button size="sm" onClick={t4}>{t5}Retry</Button>;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = registrationError instanceof Error ? registrationError : undefined;
  let t8;
  if ($[11] !== t7) {
    t8 = <CreateIssueButton error={t7} variant="outline" size="sm" />;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t6 || $[14] !== t8) {
    t9 = <EmptyContent><div className="flex flex-wrap items-center justify-center gap-2">{t6}{t8}</div></EmptyContent>;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t3 || $[17] !== t9) {
    t10 = <Empty className="border border-dashed">{t3}{t9}</Empty>;
    $[16] = t3;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function DomainReportSections(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="space-y-4">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function DomainReportClient(t0) {
  const $ = _c(40);
  const {
    domain,
    pricingTld
  } = t0;
  const trpc = useTRPC();
  let t1;
  if ($[0] !== domain || $[1] !== trpc.domain.getRegistration) {
    t1 = trpc.domain.getRegistration.queryOptions({
      domain
    }, staticQueryOptions);
    $[0] = domain;
    $[1] = trpc.domain.getRegistration;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: registration,
    isLoading: isRegistrationLoading,
    isError: isRegistrationError,
    error: registrationError
  } = useQuery(t1);
  const registrationData = registration?.success ? registration.data : undefined;
  const lookupFailed = Boolean(registration && !registration.success);
  const isRegistered = registrationData?.isRegistered === true;
  const isUnregistered = registrationData?.isRegistered === false;
  const {
    headerRef,
    isHeaderVisible,
    activeSection,
    scrollToSection
  } = useDomainReportTracking(domain, isRegistered);
  if (isRegistrationError || lookupFailed) {
    let t2;
    if ($[3] !== isRegistrationError || $[4] !== lookupFailed || $[5] !== registration || $[6] !== registrationError) {
      t2 = getReportErrorDescription({
        isRegistrationError,
        registrationError,
        lookupFailed,
        registration
      });
      $[3] = isRegistrationError;
      $[4] = lookupFailed;
      $[5] = registration;
      $[6] = registrationError;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== domain || $[9] !== registrationError || $[10] !== t2) {
      t3 = <DomainReportLoadError domain={domain} registrationError={registrationError} description={t2} />;
      $[8] = domain;
      $[9] = registrationError;
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  if (!isRegistrationLoading && isUnregistered) {
    let t2;
    if ($[12] !== domain || $[13] !== pricingTld) {
      t2 = <DomainUnregisteredCard domain={domain} pricingTld={pricingTld} />;
      $[12] = domain;
      $[13] = pricingTld;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    return t2;
  }
  const t2 = registrationData?.domainId;
  let t3;
  if ($[15] !== domain || $[16] !== headerRef || $[17] !== isRegistered || $[18] !== t2) {
    t3 = <DomainReportHeader domain={domain} domainId={t2} isRegistered={isRegistered} ref={headerRef} />;
    $[15] = domain;
    $[16] = headerRef;
    $[17] = isRegistered;
    $[18] = t2;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = Object.values(sections);
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== activeSection || $[22] !== domain || $[23] !== isHeaderVisible || $[24] !== scrollToSection) {
    t5 = <SectionNav domain={domain} sections={t4} activeSection={activeSection} isHeaderVisible={isHeaderVisible} onSectionClick={scrollToSection} />;
    $[21] = activeSection;
    $[22] = domain;
    $[23] = isHeaderVisible;
    $[24] = scrollToSection;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== domain || $[27] !== isRegistrationLoading || $[28] !== registrationData) {
    t6 = isRegistrationLoading ? <RegistrationSectionSkeleton /> : <RegistrationSection domain={domain} data={registrationData} />;
    $[26] = domain;
    $[27] = isRegistrationLoading;
    $[28] = registrationData;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== domain || $[31] !== isRegistered) {
    t7 = REPORT_SECTIONS.map(t8 => {
      const {
        id,
        sectionName,
        Loaded,
        Skeleton
      } = t8;
      return isRegistered ? <SectionErrorBoundary key={id} sectionName={sectionName}><Suspense fallback={<Skeleton />}><Loaded domain={domain} /></Suspense></SectionErrorBoundary> : <Skeleton key={id} />;
    });
    $[30] = domain;
    $[31] = isRegistered;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t6 || $[34] !== t7) {
    t8 = <DomainReportSections>{t6}{t7}</DomainReportSections>;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== t3 || $[37] !== t5 || $[38] !== t8) {
    t9 = <>{t3}{t5}{t8}</>;
    $[36] = t3;
    $[37] = t5;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
}